// Hoja de PAGE-LOAD del embed — lo único que se pinta antes de que el
// cliente pulse «Personalizar».
//
// Por qué existe (ago-2026): `injectStyles()` (styles.ts) concatena las
// 15 partes del formulario, ~165 KB de CSS que Rollup no puede
// tree-shakear porque son literales de plantilla. Se llamaba desde
// `scanAndMount`, así que ese CSS viajaba en `embed.js` y se descargaba
// en CADA page-view de ficha de producto, para pintar exactamente tres
// cosas: el botón, la barra sticky y el bloqueo del CTA nativo del tema.
// El resto (filas del formulario, editor de foto, mapas, estrellas,
// biblioteca de ideas…) sólo puede verse DENTRO del personalizador, que
// llega en su propio chunk perezoso.
//
// Regla: si un selector puede pintarse antes del primer clic, va aquí;
// si sólo se ve dentro del personalizador, va en `styles/parts/*` y lo
// inyecta `injectStyles()` desde el chunk. Ver bundle-budget.test.ts.
//
// Los bloques de abajo se movieron VERBATIM desde parts/13 (bloqueo del
// CTA nativo) y parts/12 (CTA + barra sticky); no queda ninguna regla
// `.dfp-trigger-*` en las partes, así que no hay empate de cascada entre
// las dos hojas.

import { injectFonts, injectTokens } from '@personalizer/ui-tokens';
import { BP_MD } from '../breakpoints.js';

export const CSS = /* css */ `
/* ── Storefront "hide default CTA" rules ────────────────────────────
   When the merchant links a product to a template and leaves "Ocultar
   el botón Añadir al carrito original" on, the embed sets
   <body data-personalizer-active>. These rules then hide the theme's
   native buy button, the accelerated-payment buttons AND the variant
   picker (the customer chooses the variant inside the wizard).

   This used to live only in the theme block's inline <style>
   (extensions/personalizer-embed/blocks/personalizer.liquid). Keeping
   it here too means a single "railway up" ships any change with the
   JS bundle — no Shopify theme/extension redeploy required. Gating is
   identical: every selector is scoped to body[data-personalizer-active],
   which only appears once embed.js mounts on a hide-default-CTA host,
   so admin preview / standalone are unaffected (no df-pdp markup there
   anyway). */
body[data-personalizer-active] form[action*="/cart/add"] button[name="add"],
body[data-personalizer-active] form[action*="/cart/add"] button[type="submit"],
body[data-personalizer-active] .product-form__submit,
body[data-personalizer-active] .product-form__cart-submit,
body[data-personalizer-active] .product__cart-submit,
body[data-personalizer-active] button[name="add"][type="submit"],
body[data-personalizer-active] [data-add-to-cart],
/* Donde Fue df-main-product section. */
body[data-personalizer-active] [data-df-add],
body[data-personalizer-active] .df-pdp__cta,
body[data-personalizer-active] [data-df-pdp-stickycta-trigger],
body[data-personalizer-active] .df-pdp__stickycta-cta,
body[data-personalizer-active] .df-pdp__stickycta,
/* Variant pickers — chosen in the wizard instead. */
body[data-personalizer-active] .df-pdp__variants,
body[data-personalizer-active] variant-selects,
body[data-personalizer-active] variant-radios,
body[data-personalizer-active] .product-form__variants,
body[data-personalizer-active] .product-variant-id,
body[data-personalizer-active] .product-options,
body[data-personalizer-active] .single-option-selector,
/* Shopify dynamic checkout. */
body[data-personalizer-active] .shopify-payment-button,
body[data-personalizer-active] .shopify-payment-button__button,
body[data-personalizer-active] .product-form__payment-container,
body[data-personalizer-active] .additional-checkout-buttons,
body[data-personalizer-active] [data-shopify="payment-button"] {
  display: none !important;
  visibility: hidden !important;
}
/* (Price "Desde" prefix removed 2026-06-13 at the merchant's request —
   the PDP price now renders bare, no cheapest-variant kicker. The theme
   also force-hides this ::before as a belt-and-suspenders guard against
   a stale cached embed.js — see df-main-product.liquid.) */

/* ── Trigger mode ("Personalizar" CTA) ──────────────────────────────
   Renders inside the merchant's product page where the native "Add to
   cart" button lived. The button mirrors the store's own primary
   button (trigger-cta.ts copies its fill / radius / type at mount), so
   the colours below are only a fallback for admin preview / standalone
   where no native button exists. Click → fullscreen overlay. */
.dfp-trigger-root {
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Full width of the product-info column so the CTA matches the
     variant chips / native buy button above it. Without width:100%
     the flex container shrinks to its content when the theme drops
     the host into a row-flex / inline parent (button looked ~60%
     wide, left-aligned, on the live mobile PDP). box-sizing keeps the
     padding inside that 100%. */
  width: 100%;
  max-width: 520px;
  box-sizing: border-box;
  margin: 8px 0;
}
.dfp-trigger-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 20px 32px;
  border: 0;
  border-radius: 999px;
  background: var(--dfp-accent, #C8362B);
  color: #fff;
  /* La FAMILIA se hereda del tema del comerciante a propósito: este
     botón vive en su página de producto, junto a su "Añadir al
     carrito", y debe integrarse ahí. El tamaño y el tracking sí son
     nuestros (rol trigger del sistema de diseño). */
  font: inherit;
  font-size: var(--bd-fs-trigger, 18px);
  font-weight: var(--bd-fw-trigger, 700);
  letter-spacing: var(--bd-ls-trigger, -0.012em);
  cursor: pointer;
  /* La sombra era naranja fija (#FF7A00) debajo de un botón que hoy es
     rojo: un halo de otro color que no seguía al acento de la tienda.
     Neutral y a ras, como el resto de elevaciones del sistema. */
  box-shadow: 0 1px 2px rgba(28, 24, 20, 0.12),
              0 6px 18px -8px rgba(28, 24, 20, 0.35);
  transition: transform 80ms ease, box-shadow 120ms ease, filter 120ms ease;
}
.dfp-trigger-cta:hover {
  transform: translateY(-1px);
  filter: brightness(1.05);
  box-shadow: 0 1px 2px rgba(28, 24, 20, 0.12),
              0 10px 24px -8px rgba(28, 24, 20, 0.42);
}
.dfp-trigger-cta:active {
  transform: translateY(0);
  filter: brightness(0.96);
}
.dfp-trigger-cta-arrow {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.dfp-trigger-cta-arrow svg { width: 18px; height: 18px; }

/* ── Sticky CTA bar ─────────────────────────────────────────────────
   A fixed bottom-bar copy of the trigger button, raised by
   trigger-cta.ts only while the in-flow CTA is scrolled out of view
   (an IntersectionObserver toggles the is-visible class). Minimalist: a
   translucent blurred strip the page reads through; the button inside
   wears the same cloned theme style. */
.dfp-sticky-cta-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  display: flex;
  justify-content: center;
  padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
  background: rgba(255, 255, 255, 0.82);
  -webkit-backdrop-filter: blur(12px) saturate(1.2);
  backdrop-filter: blur(12px) saturate(1.2);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 -1px 14px rgba(0, 0, 0, 0.06);
  transform: translateY(110%);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 280ms cubic-bezier(0.32, 0.72, 0.24, 1),
              opacity 200ms ease,
              visibility 0s linear 280ms;
}
.dfp-sticky-cta-bar.is-visible {
  transform: translateY(0);
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transition: transform 280ms cubic-bezier(0.32, 0.72, 0.24, 1),
              opacity 200ms ease;
}
.dfp-sticky-cta-bar .dfp-trigger-cta {
  width: 100%;
  max-width: 480px;
  padding: 15px 28px;
  font-size: 16px;
  /* Con miniatura a la izquierda, el botón es el que cede: el ancho lo
     manda su texto, no la foto. */
  min-width: 0;
}
/* La foto del diseño del cliente, cuando la hay (trigger-cta.ts). */
.dfp-sticky-cta-bar { align-items: center; gap: 10px; }
/* LA MINIATURA CONSERVA LA PROPORCIÓN DEL CARTEL, igual que la de la
   hoja de guardado. Era una caja CUADRADA de 44x44 recortando con
   «cover», y lo que guarda portada-del-cliente.ts es el cartel ENTERO
   con su marco (PORTADA_DE_LA_FICHA va sin cuadrar): de un cartel
   apaisado se veía el 71 % del ancho, medido en la tienda con una
   portada sembrada. Aquí el recorte era por los lados, o sea se comía
   justo los extremos de la palabra.
   El tope son 66 px: un A4 apaisado (1,414) pide 62 y cabe entero, y lo
   más ancho que pueda llegar —la maqueta de una taza— letterboxea un
   poco en vez de recortar. Medido: con 62 px de miniatura el botón se
   queda en 286 en un teléfono de 390 y su texto pide 250. */
.dfp-sticky-thumb {
  flex: 0 0 auto;
  height: 44px;
  width: auto;
  max-width: 66px;
  border-radius: 10px;
  object-fit: contain;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(28, 24, 20, 0.14),
              0 0 0 1px rgba(20, 17, 15, 0.07);
}
@media (max-width: ${BP_MD}px) {
  .dfp-sticky-cta-bar .dfp-trigger-cta { max-width: none; }
}

/* Copia del móvil que vivía en el media query de parts/13 junto a
   reglas del overlay: aquí sólo viaja la línea del CTA. */
@media (max-width: ${BP_MD}px) {
  .dfp-trigger-cta { font-size: 16px; padding: 18px 24px; }
}

/* Duplicado a propósito de parts/00 (mismos valores, sin var()): el
   plan de montaje puede fallar antes de que exista chunk alguno y
   scanAndMount pinta un <pre class=dfp-err> con la hoja completa
   todavía sin cargar. Las variables no están declaradas en :root con
   estos nombres fuera de .dfp-root, así que aquí van literales. */
.dfp-loading, .dfp-err {
  padding: 16px;
  border: 1px dashed #E7E1D6;
  border-radius: 12px;
  background: #FBF8F0;
  font-family: inherit;
}
.dfp-err { color: #A52A22; background: #FDECE9; border-color: #F5CDC6; }
`;

/** Inyecta la hoja de page-load. Idempotente (guarda por id), igual que
 *  `injectStyles()`. Tokens y fuentes se mantienen aquí —y no en el
 *  chunk— para no cambiar el momento en que la tienda carga Manrope /
 *  Instrument Serif: moverlos evitaría una petición a Google Fonts por
 *  page-view, pero se pagaría con un cambio de fuente visible al abrir
 *  el personalizador. Decisión aparte, medida aparte. */
export function injectTriggerStyles(): void {
  if (document.getElementById('dfp-trigger-styles')) return;
  injectTokens();
  injectFonts();
  const tag = document.createElement('style');
  tag.id = 'dfp-trigger-styles';
  tag.textContent = CSS;
  document.head.appendChild(tag);
}
